import { MatchStatus, SPORT } from "service";

type TeamType = "home" | "away";

type ScorePair = [number, number];

const SET_KEYS = ["S1", "S2", "S3", "S4", "S5"] as const;
const SPORTS_REQUIRING_TOTALS = new Set([
  SPORT.BASKETBALL,
  SPORT.TENNIS,
  SPORT.VOLLEYBALL,
]);

const ensureScorePair = (value: unknown): ScorePair => {
  if (Array.isArray(value) && value.length >= 2) {
    const home = Number(value[0]);
    const away = Number(value[1]);
    return [Number.isFinite(home) ? home : 0, Number.isFinite(away) ? away : 0];
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return [0, 0];
    const delimiter = trimmed.includes(":")
      ? ":"
      : trimmed.includes("-")
      ? "-"
      : null;
    if (!delimiter) return [0, 0];
    const [homeRaw, awayRaw] = trimmed.split(delimiter);
    const home = Number(homeRaw);
    const away = Number(awayRaw);
    return [Number.isFinite(home) ? home : 0, Number.isFinite(away) ? away : 0];
  }

  return [0, 0];
};

const getStartTimestamp = (status?: MatchStatus) => {
  const startRaw = (status as any)?.st;
  if (typeof startRaw === "number") return startRaw * 1000;
  const parsed = Number(startRaw);
  if (Number.isFinite(parsed)) return parsed * 1000;
  return null;
};

const getSecondsFromStatus = (status?: MatchStatus) => {
  if (typeof status?.tm === "number") return status.tm;
  return null;
};

const convertSecondsToMinutes = (seconds: number | null) => {
  if (typeof seconds !== "number" || Number.isNaN(seconds)) return null;
  const minutes = Math.floor(seconds / 60);
  return minutes < 0 ? 0 : minutes;
};

const formatUpcomingMatchLabel = (status?: MatchStatus) => {
  const startTimestamp = getStartTimestamp(status);
  if (startTimestamp) {
    const diffMs = startTimestamp - Date.now();
    if (diffMs > 0) {
      const minutes = Math.ceil(diffMs / 60000);
      if (minutes > 0) {
        return `${minutes} min${minutes === 1 ? "" : "s"}`;
      }
    }
  }

  const seconds = getSecondsFromStatus(status);
  if (typeof seconds === "number" && seconds < 0) {
    const minutes = Math.ceil(Math.abs(seconds) / 60);
    if (minutes > 0) {
      return `${minutes} min${minutes === 1 ? "" : "s"}`;
    }
  }

  return null;
};

const formatSetScoreLabel = (
  status: MatchStatus | undefined,
  prefix: string
) => {
  if (!status) return `${prefix.trim()} --`;
  const courtData = ((status as any)?.c ?? {}) as Record<string, unknown>;
  const rawPc = typeof status?.pc === "number" ? status.pc : undefined;
  const normalizedPc =
    rawPc && rawPc >= 1 ? Math.min(rawPc, SET_KEYS.length) : undefined;

  const pickSet = (index?: number) => {
    if (!index) return null;
    const pair = ensureScorePair(courtData[`S${index}`]);
    if (pair[0] === 0 && pair[1] === 0) return null;
    return { index, pair };
  };

  let selection = pickSet(normalizedPc);

  if (!selection) {
    for (let i = SET_KEYS.length; i >= 1; i -= 1) {
      const pair = ensureScorePair(courtData[`S${i}`]);
      if (pair[0] !== 0 || pair[1] !== 0) {
        selection = { index: i, pair };
        break;
      }
    }
  }

  const index = selection?.index ?? normalizedPc ?? 1;
  const pair = selection?.pair ?? ensureScorePair(courtData[`S${index}`]);
  const label = `${prefix}${index}`.trim();
  return `${label} ${pair[0]}-${pair[1]}`.trim();
};

const formatDefaultMinuteLabel = (status?: MatchStatus) => {
  const minute = convertSecondsToMinutes(getSecondsFromStatus(status));
  if (typeof minute === "number") return `${minute}'`;
  const fallbackPc = typeof status?.pc === "number" ? status.pc : null;
  if (typeof fallbackPc === "number") return `${fallbackPc}'`;
  return "--'";
};

const formatFootballMinuteLabel = (status?: MatchStatus) => {
  const minute = convertSecondsToMinutes(getSecondsFromStatus(status));
  const fallbackPc = typeof status?.pc === "number" ? status.pc : null;
  const effectiveMinute =
    typeof minute === "number"
      ? minute
      : typeof fallbackPc === "number"
      ? fallbackPc
      : null;
  if (typeof effectiveMinute === "number") {
    if (effectiveMinute >= 90) return "90+";
    return `${effectiveMinute}'`;
  }
  return "--'";
};

const formatBasketballMinuteLabel = (status?: MatchStatus) => {
  const minute = convertSecondsToMinutes(getSecondsFromStatus(status));
  const quarter =
    typeof status?.pc === "number" && status.pc >= 1
      ? `Q${status.pc}`
      : undefined;
  const parts: string[] = [];
  if (quarter) parts.push(quarter);
  if (typeof minute === "number") parts.push(`${minute}'`);
  if (parts.length) return parts.join(" ");
  return "--'";
};

const formatIceHockeyMinuteLabel = (status?: MatchStatus) => {
  const minute = convertSecondsToMinutes(getSecondsFromStatus(status));
  const period =
    typeof status?.pc === "number" && status.pc >= 1
      ? `P${status.pc}`
      : undefined;
  const isOvertime = status?.nsc === "1281";
  const periodLabel = isOvertime ? "OT" : period;
  if (typeof minute === "number" && periodLabel)
    return `${periodLabel} ${minute}'`;
  if (typeof minute === "number") return `${minute}'`;
  if (periodLabel) return periodLabel;
  return "--'";
};

const formatSetDrivenMinuteLabel = (
  status: MatchStatus | undefined,
  prefix: string
) => formatSetScoreLabel(status, prefix);

const formatMinuteLabelBySport = (
  status: MatchStatus | undefined,
  fallbackSport: SPORT
) => {
  const upcomingLabel = formatUpcomingMatchLabel(status);
  if (upcomingLabel) return upcomingLabel;

  switch (fallbackSport) {
    case SPORT.FOOTBALL:
      return formatFootballMinuteLabel(status);
    case SPORT.BASKETBALL:
      return formatBasketballMinuteLabel(status);
    case SPORT.TENNIS:
      return formatSetDrivenMinuteLabel(status, "Set ");
    case SPORT.VOLLEYBALL:
      return formatSetDrivenMinuteLabel(status, "Set ");
    case SPORT.ICE_HOCKEY:
      return formatIceHockeyMinuteLabel(status);
    default:
      return formatDefaultMinuteLabel(status);
  }
};

export const formatMinuteLabel = (
  status: MatchStatus | undefined,
  fallbackSport: SPORT
) => formatMinuteLabelBySport(status, fallbackSport);

export const resolveTeamName = (
  status: MatchStatus | undefined,
  fallback: string,
  type: TeamType
) => {
  const home = status?.T1?.n ?? status?.t1?.n ?? status?.T1n;
  const away = status?.T2?.n ?? status?.t2?.n ?? status?.T2n;
  const resolved = type === "home" ? home : away;
  if (typeof resolved === "string" && resolved.trim().length > 0) {
    return resolved;
  }

  const fallbackLabel =
    typeof fallback === "string" && fallback.trim().length > 0
      ? fallback
      : String(fallback ?? "").trim();

  if (fallbackLabel) return fallbackLabel;

  return type === "home" ? "Home" : "Away";
};

export const resolveScore = (
  status: MatchStatus | undefined,
  fallbackSport: SPORT
): ScorePair => {
  if (!status) return [0, 0];
  const courtData = ((status as any)?.c ?? {}) as Record<string, unknown>;

  const totals = (courtData as any)?.T;
  if (totals !== undefined && totals !== null) {
    return ensureScorePair(totals);
  }

  if (fallbackSport === SPORT.BASEBALL) {
    const runs = (courtData as any)?.R;
    if (runs !== undefined && runs !== null) {
      return ensureScorePair(runs);
    }
  }

  if (SPORTS_REQUIRING_TOTALS.has(fallbackSport)) {
    return [0, 0];
  }

  const goal = (courtData as any)?.GOAL;
  if (Array.isArray(goal) && goal.length >= 2) {
    return [Number(goal[0]) || 0, Number(goal[1]) || 0];
  }

  if (typeof status.sc === "string") {
    const [home, away] = status.sc.split("-").map((item) => {
      const parsed = Number(item.trim());
      return Number.isNaN(parsed) ? 0 : parsed;
    });
    return [Number.isFinite(home) ? home : 0, Number.isFinite(away) ? away : 0];
  }

  return [0, 0];
};
